// src/pages/FootballGames.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styles from "../styles/Fixtures.module.css";
import { api } from "../api"; // env-based axios client

const FINISHED_STATUSES = ["FT", "AET", "PEN", "FINISHED", "FULLTIME"];
const LIVE_STATUSES = ["1H", "2H", "HT", "ET", "LIVE", "INPLAY", "BREAK", "INT"];

const fixtureStatusMeta = (f) => {
  const fixtureObj = typeof f?.fixture === "object" && f?.fixture ? f.fixture : null;
  const statusObj = typeof f?.status === "object" && f?.status ? f.status : null;
  const nestedStatusObj = typeof fixtureObj?.status === "object" && fixtureObj?.status ? fixtureObj.status : null;
  const raw = String(
    f?.status_short ||
    fixtureObj?.status_short ||
    statusObj?.short ||
    nestedStatusObj?.short ||
    statusObj?.code ||
    nestedStatusObj?.code ||
    f?.match_status ||
    f?.fixture_status ||
    f?.status ||
    ""
  ).toUpperCase();
  const elapsed = Number(
    f?.elapsed ??
    f?.status_elapsed ??
    f?.live_minute ??
    f?.minute ??
    statusObj?.elapsed ??
    nestedStatusObj?.elapsed
  );
  const isFinished = FINISHED_STATUSES.includes(raw);
  const isLive = LIVE_STATUSES.includes(raw) || (!isFinished && Number.isFinite(elapsed) && elapsed > 0);
  return { raw, elapsed, isFinished, isLive };
};

const fixtureScoreMeta = (f) => {
  const fixtureObj = typeof f?.fixture === "object" && f?.fixture ? f.fixture : null;
  const home =
    f?.home_score ??
    f?.goals_home ??
    f?.score_home ??
    f?.score?.home ??
    f?.score?.fulltime?.home ??
    f?.score?.full_time?.home ??
    f?.scores?.home ??
    f?.scores?.fulltime?.home ??
    fixtureObj?.goals?.home ??
    fixtureObj?.score?.home ??
    fixtureObj?.score?.fulltime?.home ??
    f?.goals?.home;
  const away =
    f?.away_score ??
    f?.goals_away ??
    f?.score_away ??
    f?.score?.away ??
    f?.score?.fulltime?.away ??
    f?.score?.full_time?.away ??
    f?.scores?.away ??
    f?.scores?.fulltime?.away ??
    fixtureObj?.goals?.away ??
    fixtureObj?.score?.away ??
    fixtureObj?.score?.fulltime?.away ??
    f?.goals?.away;
  return {
    home,
    away,
    hasScore: home !== null && home !== undefined && away !== null && away !== undefined,
  };
};

const FootballGames = () => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Helper: format kickoff in user timezone ----
  const formatKickoff = (utcString, withDate = false) => {
    if (!utcString) return "";
    // already has Z or ±HH:MM offset → leave alone; otherwise append Z
    const kickoff = /Z$|[+-]\d{2}:\d{2}$/.test(utcString) ? utcString : utcString + "Z";
    return new Date(kickoff).toLocaleString(navigator.language, {
      weekday: withDate ? "short" : undefined,
      day: withDate ? "numeric" : undefined,
      month: withDate ? "short" : undefined,
      hour: "2-digit",
      minute: "2-digit",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  useEffect(() => {
    const fetchFixtures = async () => {
      try {
        // Fetch today + tomorrow, filtered to football only server-side
        const all = [];
        const today = new Date();
        for (let i = 0; i < 2; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() + i);
          const day = d.toISOString().slice(0, 10);
          const { data } = await api.get("/api/public/fixtures/daily", { params: { day, sport: "football" } });
          if (data?.fixtures) all.push(...data.fixtures);
        }
        setFixtures(all);
      } catch (err) {
        console.error("Error fetching football fixtures:", err);
        setFixtures([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFixtures();
  }, []);

  if (loading) return <p className={styles.loading}>Loading football fixtures...</p>;
  if (!fixtures.length) return <p className={styles.empty}>No football fixtures available.</p>;

  // Group by competition + date
  const grouped = fixtures.reduce((acc, f) => {
    const date = formatKickoff(f.kickoff_utc, true); // includes weekday + date
    const key = `${f.comp} • ${date}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(f);
    return acc;
  }, {});

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>Football Fixtures</h2>
      {Object.keys(grouped).map((group) => (
        <div key={group} className={styles.group}>
          <h3 className={styles.groupTitle}>{group}</h3>
          <div className={styles.cards}>
            {grouped[group].map((f) => (
              <Link key={f.id} to={`/fixture/${f.id}`} className={styles.card}>
                {(() => {
                  const status = fixtureStatusMeta(f);
                  const score = fixtureScoreMeta(f);
                  const statusLabel =
                    status.isFinished ? "FT" : status.raw === "HT" ? "HT" : status.isLive ? (Number.isFinite(status.elapsed) ? `${status.elapsed}'` : "LIVE") : "";
                  const showScore = score.hasScore && (status.isLive || status.isFinished);
                  return (
                    <>
                      <div className={styles.teams}>
                        {f.home_team} <span className={styles.vs}>vs</span> {f.away_team}
                      </div>
                      <div className={styles.kickoff}>
                        {showScore ? `${score.home} - ${score.away}` : formatKickoff(f.kickoff_utc)}
                        {statusLabel ? <span style={{ marginLeft: 8, fontWeight: 700 }}>{statusLabel}</span> : null}
                      </div>
                    </>
                  );
                })()}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FootballGames;
